import { FC, useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@progress/kendo-react-buttons";
import { Card, CardBody, CardImage } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import { FormFor } from "@excent-one/react";
import { ControlledInput } from "@excent-one/react/form/inputs";
import { useAuthContext, AuthStatus } from "@excent-one/auth";
import type { ArbitrIasLoginArgs } from "@phrmpc/infrastructure/auth/arbitr-ias";
import { authEnabled } from "@phrmpc/app/config";
import paramountLogo from "@phrmpc/assets/images/brand/2x/paramount_logo_vertical.png";
import styles from "./Login.module.scss";
import { FormFieldSet, FormSeparator } from "@progress/kendo-react-all";

interface LoginFormValues {
  email: string;
  password: string;
}

const defaultValues: LoginFormValues = {
  email: "",
  password: "",
};

const { Form } = FormFor<LoginFormValues>();

const Login: FC = () => {
  const [{ status }, { login }] = useAuthContext();
  const navigate = useNavigate();
  const target = "/dashboard";

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authEnabled) {
      navigate({ to: target, replace: true });
      return;
    }
    if (status === AuthStatus.LoggedIn) {
      navigate({ to: target, replace: true });
    }
  }, [status, target, navigate]);

  const isBusy = status === AuthStatus.LoggingIn;

  const onSubmit = async (values: LoginFormValues) => {
    setError(null);
    try {
      // login()'s own parameter type is @excent-one/auth's base LoginArgs, which doesn't declare
      // `credentials` (a type alias, so it can't be widened via declare module merging -- see
      // ArbitrIasLoginArgs's own doc comment). `satisfies` still validates the object's shape
      // against ArbitrIasLoginArgs; the `as` only suppresses login()'s own narrower signature.
      await login({
        redirectTo: target,
        credentials: { email: values.email, password: values.password },
      } satisfies ArbitrIasLoginArgs as Parameters<typeof login>[0]);
    } catch (err) {
      setError(
        err instanceof TypeError
          ? "Unable to reach the server. Please check your connection and try again."
          : "Invalid email or password.",
      );
    }
    return values;
  };

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <CardImage
          src={paramountLogo}
          alt="Paramount"
          className={styles.logo}
        />
        {/* <FormSeparator /> */}
        <CardBody className={styles.cardBody}>
          {/* <Typography.p className={styles.subtitle}>
            Sign in to continue
          </Typography.p> */}
          <Form
            className={styles.form}
            defaultValues={defaultValues}
            onSubmit={onSubmit}
            disabled={isBusy}
          >
            {({ control }) => (
              <>
                <FormFieldSet
                  cols={1}
                  legend="Sign in to continue"
                  className="k-text-center"
                >
                  <ControlledInput
                    control={control}
                    name="email"
                    type="email"
                    label="Email"
                    required
                    disabled={isBusy}
                  />
                  <ControlledInput
                    control={control}
                    name="password"
                    type="password"
                    label="Password"
                    required
                    disabled={isBusy}
                  />
                </FormFieldSet>
                {error && (
                  <Typography.p className={styles.error}>{error}</Typography.p>
                )}
                <FormSeparator />
                <Button
                  themeColor="primary"
                  size="large"
                  type="submit"
                  disabled={isBusy}
                >
                  {isBusy ? "Signing in…" : "Sign in"}
                </Button>
              </>
            )}
          </Form>
        </CardBody>
      </Card>
    </div>
  );
};

export default Login;
